import React, {useEffect, useState} from "react";
import {T, useTranslate} from "@tolgee/react";
import {
  Button,
  CircularProgress,
  IconButton,
  TextField,
  Typography,
} from "@material-ui/core";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import CloseIcon from "@material-ui/icons/Close";
import MailOutlineIcon from "@material-ui/icons/MailOutline";
import {useSelector} from "react-redux";
import {useEmailTwoFactor} from "store/two-factor/email-two-factor.store";
import {useTwoFactorSettings} from "store/two-factor/two-factor-settings.store";
import {selectUser} from "store/auth/selectors";
import {useShallow} from "zustand/react/shallow";
import {formatCountdown} from "tools/formats/formatCountdown";
import {formatISODate} from "tools/formats/formatDate";
import {maskEmail} from "tools/formats/maskEmail";
import {emailValidationPattern} from "tools/validations";
import useDateFnsLocale from "hooks/useDateFnsLocale";
import ConfirmPasswordModal from "view/components/ConfirmPasswordModal";
import Alert from "view/components/Alert";
import CodeInput from "view/components/CodeInput";
import RecoveryCodes from "view/components/RecoveryCodes";
import useStyles from "./styles";

type Props = {
  onBack: () => void;
};

const SAME_AS_SIGN_IN_MESSAGE_KEY = "common.two_factor.email.same_as_sign_in";
const INVALID_EMAIL_MESSAGE = "Enter a valid email address.";
const EMAIL_RESEND_COOLDOWN_SECONDS = 35;

const EmailTwoFactorManager: React.FC<Props> = ({onBack}) => {
  const classes = useStyles();
  const {t} = useTranslate();
  const locale = useDateFnsLocale();

  const {
    status,
    pendingEnable,
    recoveryCodes,
    fetchStatus,
    beginEnable,
    verifyEnable,
    revoke,
    clearPendingEnable,
    clearRecoveryCodes,
  } = useEmailTwoFactor(
    useShallow((state) => ({
      status: state.status,
      pendingEnable: state.pendingEnable,
      recoveryCodes: state.recoveryCodes,
      fetchStatus: state.fetchStatus,
      beginEnable: state.beginEnable,
      verifyEnable: state.verifyEnable,
      revoke: state.revoke,
      clearPendingEnable: state.clearPendingEnable,
      clearRecoveryCodes: state.clearRecoveryCodes,
    }))
  );

  const confirmPassword = useTwoFactorSettings(
    (state) => state.confirmPassword
  );

  const {email: signInEmail, hasPassword} = useSelector(selectUser);

  const [isConfirmingPassword, setIsConfirmingPassword] = useState(false);
  const [isConfirmingRevoke, setIsConfirmingRevoke] = useState(false);
  const [passwordlessSetup, setPasswordlessSetup] = useState(false);
  const [confirmedPassword, setConfirmedPassword] = useState<string | null>(
    null
  );
  const [email, setEmail] = useState("");
  const [setupError, setSetupError] = useState<string | null>(null);
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [code, setCode] = useState("");
  const [verifyError, setVerifyError] = useState<string | null>(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [justAdded, setJustAdded] = useState(false);
  const [lastEnableParams, setLastEnableParams] = useState<{
    password?: string;
    email: string;
  } | null>(null);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [isResending, setIsResending] = useState(false);
  const [resendError, setResendError] = useState<string | null>(null);

  const isSameAsSignIn =
    !!email &&
    !!signInEmail &&
    email.trim().toLowerCase() === signInEmail.trim().toLowerCase();

  const emailError = !email
    ? null
    : !emailValidationPattern.test(email.trim())
    ? INVALID_EMAIL_MESSAGE
    : isSameAsSignIn
    ? t(SAME_AS_SIGN_IN_MESSAGE_KEY)
    : null;

  useEffect(() => {
    fetchStatus();
    return () => {
      clearPendingEnable();
      clearRecoveryCodes();
    };
  }, [fetchStatus, clearPendingEnable, clearRecoveryCodes]);

  useEffect(() => {
    if (resendCooldown <= 0) return undefined;

    const timeout = setTimeout(() => {
      setResendCooldown((seconds) => seconds - 1);
    }, 1000);

    return () => clearTimeout(timeout);
  }, [resendCooldown]);

  const resetEmailEntry = () => {
    setConfirmedPassword(null);
    setPasswordlessSetup(false);
    setEmail("");
    setSetupError(null);
  };

  const handleConfirmPassword = async (password: string) => {
    await confirmPassword(password);
    setConfirmedPassword(password);
    setIsConfirmingPassword(false);
  };

  const handleConfirmRevoke = async (password?: string) => {
    await revoke(password);
    setIsConfirmingRevoke(false);
  };

  const handleSendCode = async () => {
    if (!confirmedPassword && !passwordlessSetup) return;
    if (!email || emailError) return;

    setSetupError(null);
    setIsSendingCode(true);
    try {
      await beginEnable(confirmedPassword ?? undefined, email.trim());
      setLastEnableParams({
        password: confirmedPassword ?? undefined,
        email: email.trim(),
      });
      setResendCooldown(EMAIL_RESEND_COOLDOWN_SECONDS);
      setConfirmedPassword(null);
      setPasswordlessSetup(false);
      setEmail("");
    } catch (e) {
      const errorCode = (e as {response?: {data?: {message?: string}}})
        ?.response?.data?.message;
      setSetupError(
        errorCode === "EMAIL_TWO_FACTOR_SAME_AS_SIGN_IN"
          ? t(SAME_AS_SIGN_IN_MESSAGE_KEY)
          : t("common.error.try_again")
      );
    } finally {
      setIsSendingCode(false);
    }
  };

  const handleResendCode = async () => {
    if (!lastEnableParams || resendCooldown > 0) return;

    setResendError(null);
    setIsResending(true);
    try {
      await beginEnable(lastEnableParams.password, lastEnableParams.email);
      setResendCooldown(EMAIL_RESEND_COOLDOWN_SECONDS);
    } catch (e) {
      setResendError(t("common.error.try_again"));
    } finally {
      setIsResending(false);
    }
  };

  const handleCancelVerify = () => {
    if (pendingEnable) clearPendingEnable();
    setCode("");
    setVerifyError(null);
    setLastEnableParams(null);
    setResendCooldown(0);
    setResendError(null);
  };

  const handleVerify = async (submittedCode: string = code, silent = false) => {
    setVerifyError(null);
    setIsVerifying(true);
    try {
      if (pendingEnable) {
        await verifyEnable(submittedCode);
        setJustAdded(true);
      }
      setCode("");
    } catch (e) {
      if (silent) return;
      const status = (e as {response?: {status?: number}})?.response?.status;
      const errorCode = (e as {response?: {data?: {message?: string}}})
        ?.response?.data?.message;
      if (status === 429) {
        setVerifyError(t("common.two_factor.too_many_attempts"));
      } else if (errorCode?.endsWith("_NOT_STARTED")) {
        setVerifyError(t("common.two_factor.code_expired"));
      } else {
        setVerifyError(t("common.two_factor.incorrect_code"));
      }
    } finally {
      setIsVerifying(false);
    }
  };

  const renderEmailEntry = () => (
    <div className={classes.step}>
      <Typography className={classes.helper}>
        <T keyName="common.two_factor.email.enter_address" />
      </Typography>
      <TextField
        label={t("common.two_factor.email.address_label")}
        type="email"
        name="email-two-factor-address"
        variant="outlined"
        fullWidth
        placeholder={t("common.two_factor.email.address_placeholder")}
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        disabled={isSendingCode}
        error={!!emailError}
        helperText={emailError ?? undefined}
        autoFocus
        autoComplete="off"
        inputProps={{autoComplete: "off"}}
      />
      {setupError && (
        <div className={classes.alertWrapper}>
          <Alert severity="error" fullWidth>
            {setupError}
          </Alert>
        </div>
      )}
      <div className={classes.actionsRow}>
        <Button
          color="primary"
          disabled={isSendingCode}
          onClick={resetEmailEntry}
        >
          <T keyName="common.action.cancel" />
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={isSendingCode || !email || !!emailError}
          endIcon={
            isSendingCode ? (
              <CircularProgress size={18} color="inherit" />
            ) : undefined
          }
          onClick={handleSendCode}
        >
          <T keyName="common.two_factor.send_code" />
        </Button>
      </div>
    </div>
  );

  const renderVerifyCode = () => (
    <div className={classes.step}>
      <Typography className={classes.helper}>
        <T keyName="common.two_factor.email.enable_code_prompt" />
      </Typography>
      <CodeInput
        value={code}
        onChange={setCode}
        disabled={isVerifying}
        autoFocus
        onComplete={handleVerify}
      />
      {verifyError && (
        <div className={classes.alertWrapper}>
          <Alert severity="error" fullWidth>
            {verifyError}
          </Alert>
        </div>
      )}
      {resendError && (
        <div className={classes.alertWrapper}>
          <Alert severity="error" fullWidth>
            {resendError}
          </Alert>
        </div>
      )}
      <div className={classes.buttonWrapper}>
        <Button
          color="primary"
          disabled={isResending || resendCooldown > 0 || !lastEnableParams}
          endIcon={
            isResending ? (
              <CircularProgress size={18} color="inherit" />
            ) : undefined
          }
          onClick={handleResendCode}
        >
          {/* Same formatter as the SMS timers rather than a hardcoded `0:`.
              The email cooldown is a flat client-side 35s today, so the output
              is identical — but the hardcoded minute would have printed `0:90`
              the moment that number moved past a minute. */}
          {resendCooldown > 0
            ? `Resend Email (${formatCountdown(resendCooldown)})`
            : "Resend Email"}
        </Button>
      </div>
      <div className={classes.actionsRow}>
        <Button
          color="primary"
          disabled={isVerifying}
          onClick={handleCancelVerify}
        >
          <T keyName="common.action.cancel" />
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={isVerifying || code.length < 6}
          endIcon={
            isVerifying ? (
              <CircularProgress size={18} color="inherit" />
            ) : undefined
          }
          onClick={() => handleVerify()}
        >
          <T keyName="common.action.verify" />
        </Button>
      </div>
    </div>
  );

  const renderManage = () => (
    <>
      {status?.configured ? (
        <div className={classes.rowCard}>
          <div className={classes.iconBox}>
            <MailOutlineIcon />
          </div>
          <div className={classes.rowContent}>
            <Typography className={classes.rowTitle}>
              {status.email ? maskEmail(status.email) : ""}
            </Typography>
            <Typography className={classes.rowSubtitle}>
              {status.verifiedAt
                ? t("common.two_factor.enabled_on", {
                    date: formatISODate(
                      status.verifiedAt,
                      "d MMMM yyyy",
                      locale
                    ),
                  })
                : t("common.two_factor.enabled")}
            </Typography>
          </div>
          <IconButton
            size="small"
            onClick={() => {
              if (hasPassword) {
                setIsConfirmingRevoke(true);
              } else {
                handleConfirmRevoke();
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </div>
      ) : (
        status !== null && (
          <Typography variant="body2" className={classes.emptyText}>
            <T keyName="common.two_factor.email.description" />
          </Typography>
        )
      )}
      {!status?.configured && (
        <div className={classes.buttonWrapper}>
          <Button
            variant="outlined"
            color="primary"
            onClick={() => {
              if (hasPassword) {
                setIsConfirmingPassword(true);
              } else {
                setPasswordlessSetup(true);
              }
            }}
          >
            <T keyName="common.two_factor.email.enable_button" />
          </Button>
        </div>
      )}
    </>
  );

  return (
    <>
      <div className={classes.backRow}>
        <IconButton size="small" onClick={onBack}>
          <ArrowBackIcon />
        </IconButton>
        <Typography className={classes.backTitle}>
          {recoveryCodes ? (
            <T keyName="common.two_factor.recovery.save_title" />
          ) : (
            <T keyName="common.two_factor.method.email" />
          )}
        </Typography>
      </div>

      {justAdded && (
        <div
          className={
            recoveryCodes
              ? `${classes.alertWrapper} ${classes.alertInset}`
              : classes.alertWrapper
          }
        >
          <Alert severity="success" fullWidth>
            Email added as a 2-Step Verification method
          </Alert>
        </div>
      )}

      {recoveryCodes ? (
        <RecoveryCodes codes={recoveryCodes} onDone={clearRecoveryCodes} />
      ) : pendingEnable ? (
        renderVerifyCode()
      ) : confirmedPassword || passwordlessSetup ? (
        renderEmailEntry()
      ) : (
        renderManage()
      )}

      {isConfirmingPassword && (
        <ConfirmPasswordModal
          subtitle={t("common.two_factor.method.email")}
          description={t("common.two_factor.email.setup_prompt")}
          submitTitle={t("common.action.continue")}
          onClose={() => setIsConfirmingPassword(false)}
          onConfirm={handleConfirmPassword}
        />
      )}
      {isConfirmingRevoke && (
        <ConfirmPasswordModal
          subtitle={t("common.two_factor.method.email")}
          submitTitle={t("common.action.continue")}
          onClose={() => setIsConfirmingRevoke(false)}
          onConfirm={handleConfirmRevoke}
        />
      )}
    </>
  );
};

export default EmailTwoFactorManager;
